import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Coupon, CouponConfiguration } from '../common/contracts';
import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import {
  ApiRoutes,
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock
} from './api.utils';

export interface PromoCodeDetailsData {
  name: string;
  maxRedemptions: number;
  active: boolean;
}

export interface CouponDetailsData {
  name: string;
  active: boolean;
  discountPercentage: number;
  maxRedemptionsPerCustomer: number | null;
  expiredBy: string | null;
  supportedOffersByName: string[];
  supportedOffersByType: string[];
  firstTimePurchase: boolean;
  promoCodes: PromoCodeDetailsData[];
}

export default function useCoupons(
  currentPublisherId?: string | null,
  couponId?: string | null
) {
  const axios = useAxios();
  const queryClient = useQueryClient();

  const getCoupons = useQuery({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.COUPONS, currentPublisherId],
    queryFn: async () => {
      const coupons = await axios.get(
        getApiRouteOrMock(EApiRoutes.COUPONS),
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return coupons as Coupon[];
    },
    enabled: !!currentPublisherId
  });

  const getCouponsConfiguration = useQuery({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.COUPONS_CONFIGURATION, currentPublisherId],
    queryFn: async () => {
      const coupons = await axios.get(
        getApiRouteOrMock(EApiRoutes.COUPONS_CONFIGURATION),
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return coupons as CouponConfiguration;
    },
    enabled: !!currentPublisherId
  });

  const getSingleCoupon = useQuery({
    queryKey: [EQueryKeys.SINGLE_COUPON, couponId],
    queryFn: async () => {
      const coupon = await axios.get(
        `${ApiRoutes.COUPONS}/${couponId}`,
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return coupon as Coupon;
    },
    enabled: !!couponId && !!currentPublisherId
  } as const);

  const updateCoupon = useMutation({
    mutationFn: async (couponData: any) => {
      const response = await axios.put(
        `${ApiRoutes.COUPONS}/${couponId}`,
        couponData,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    },
    onSuccess: async () =>
      queryClient.invalidateQueries({
        queryKey: [EQueryKeys.SINGLE_COUPON, couponId]
      })
  });

  const updateCouponsConfiguration = useMutation({
    mutationFn: async (couponsConfiguration: CouponConfiguration) => {
      const response = await axios.put(
        getApiRouteOrMock(EApiRoutes.COUPONS_CONFIGURATION),
        couponsConfiguration,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    },
    onSuccess: async () =>
      queryClient.invalidateQueries({
        queryKey: [EQueryKeys.COUPONS_CONFIGURATION, currentPublisherId]
      })
  });

  const setCouponActive = useMutation(
    async ({ couponId, active }: { couponId: string; active: boolean }) => {
      const response = await axios.put(
        `${ApiRoutes.COUPONS}/${couponId}/setActivation`,
        { active },
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  );

  const addCoupon = useMutation(
    async (couponData: Partial<CouponDetailsData>) => {
      const response = await axios.post(
        getApiRouteOrMock(EApiRoutes.COUPONS),
        couponData,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  );

  const validatePromoCodeName = useMutation(async (promoCodeName: string) => {
    const response = await axios.get(
      `${getApiRouteOrMock(EApiRoutes.PROMO_CODE)}/${promoCodeName}`,
      {},
      currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
    );
    return response.promoCode;
  });

  return {
    getCoupons,
    getSingleCoupon,
    updateCoupon,
    addCoupon,
    setCouponActive,
    validatePromoCodeName,
    updateCouponsConfiguration,
    getCouponsConfiguration
  };
}
